import Label from "components/Label";
import useComponentDimensions from "hooks/use-component-dimensions";
import React, { FC, useEffect, useState } from "react";
import { motion } from "framer-motion";

import useComponentPosition from "hooks/use-component-position";
import useScreenSize from "hooks/use-screen-size";
import { SLIDE_IN_X } from "utils/animations";
import Hexagon from "../../../../../public/hh-shape.svg";
import Separator from "./components/Separator";
import styles from "./styles.module.scss";

type Props = {
  title: string;
  description: string;
  isLastStep: boolean;
  stepNumber: number;
  onStepChange(newStep: number): void;
  index: number;
};

const Step: FC<Props> = ({
  title,
  description,
  isLastStep,
  stepNumber,
  onStepChange,
  index
}) => {
  const { height, measure } = useComponentDimensions();
  const { top, ref } = useComponentPosition();
  const { screenHeight, isResponsive } = useScreenSize();
  const [isSelected, setIsSelected] = useState(false);

  useEffect(() => {
    if (top !== null) {
      setIsSelected((prevState) => {
        const selected = top < screenHeight / 2;

        if (selected && !prevState) onStepChange(index);
        if (!selected && prevState && index - 1 >= 0) onStepChange(index - 1);

        return selected;
      });
    }
  }, [top]);

  return (
    <motion.div {...SLIDE_IN_X(-100)} className={styles.step} ref={ref}>
      <div className={styles.top}>
        <motion.div
          animate={
            isSelected
              ? isResponsive
                ? "selectedResp"
                : "selected"
              : isResponsive
              ? "notSelectedResp"
              : "notSelected"
          }
          className={styles.stepNumber}
        >
          <motion.div
            variants={{
              selected: { width: 70, height: 70 },
              notSelected: { width: 55, height: 55 },
              selectedResp: { width: 50, height: 50 },
              notSelectedResp: { width: 45, height: 45 }
            }}
          >
            <Hexagon
              className={`${styles.hex} ${isSelected && styles.selectedHex}`}
            />
          </motion.div>
          <motion.div
            variants={{
              selected: { fontSize: "25px" },
              notSelected: { fontSize: "20px" },
              selectedResp: { fontSize: "20px" },
              notSelectedResp: { fontSize: "15px" }
            }}
            className={styles.stepNumberLabel}
          >
            {stepNumber}
          </motion.div>
        </motion.div>
        <Label variant="h4" color={isSelected ? "primary" : "supplemental"}>
          {title}
        </Label>
      </div>
      <div
        style={{ height: height || "fit-content" }}
        className={styles.bottom}
      >
        <Separator isSelected={isSelected} show={!isLastStep} />
        <Label
          ref={measure}
          variant="s3"
          color={isSelected ? "primary" : "supplemental"}
        >
          {description}
        </Label>
      </div>
    </motion.div>
  );
};

export default Step;
